// Phase 2 of the lesson system: per-section practice history and the
// error-driven lesson generator. Every completed judged pass over a
// section's A/B loop feeds recordPass; sections climb a rate ladder
// (60% → 80% → 100%) by consecutive clean passes — errors practiced are
// errors learned, so a wrong or missed note resets the streak — and
// mastered sections come back as spaced reviews. Pure; time is `now`.

{max, min, round} := Math

{ accuracy } from ../midi/game-score.civet

export type PassStats =
  perfect: number
  good: number
  early: number
  late: number
  wrong: number
  miss: number

export type PassInfo =
  stats: PassStats
  // playback rate the pass was played at
  rate: number
  // the pass reached the end of the loop (an abandoned pass never counts)
  complete: boolean

export RATE_STEPS := [0.6, 0.8, 1]
export CLEAN_TO_GRADUATE := 2
REVIEW_AFTER_MS := 3 * 24 * 60 * 60 * 1000

export type SectionProgress =
  passes: number
  cleanStreak: number
  // index into RATE_STEPS the learner is working at
  step: number
  mastered: boolean
  // exponential moving average of pass accuracy, 0..1
  accuracy: number
  lastAt: number

// keyed by section index
export type PieceProgress = Record<string, SectionProgress>

judgedCount := (s: PassStats) ->
  s.perfect + s.good + s.early + s.late + s.miss

// The game's quality-weighted accuracy, by definition: section graduation
// must track the number the results screen shows
export accuracyOf := (s: PassStats) -> accuracy s

export newSectionProgress := (): SectionProgress ->
  passes: 0
  cleanStreak: 0
  step: 0
  mastered: false
  accuracy: 0
  lastAt: 0

export targetRate := (p: SectionProgress) ->
  RATE_STEPS[min p.step, RATE_STEPS.length - 1]

export recordPass := (progress: PieceProgress, section: number, info: PassInfo, now: number): void ->
  return unless info.complete
  {stats} := info
  return unless judgedCount(stats) > 0

  p := progress[section] ?? newSectionProgress()
  acc := accuracyOf stats
  p.passes++
  p.accuracy = if p.passes is 1 then acc else 0.6 * p.accuracy + 0.4 * acc
  p.lastAt = now

  clean := stats.wrong is 0 and stats.miss is 0
  if clean
    // a clean pass only graduates at (or above) the working rate — playing
    // slower than the target is practice, not progress
    if info.rate >= targetRate(p) - 0.051
      p.cleanStreak++
      if p.cleanStreak >= CLEAN_TO_GRADUATE
        if p.step < RATE_STEPS.length - 1
          p.step++
          p.cleanStreak = 0
        else
          p.mastered = true
  else
    p.cleanStreak = 0
    // a mastered section that falls apart comes back into rotation
    p.mastered = false

  progress[section] = p
  return

export type PlanItem =
  section: number
  name: string
  rate: number
  // wait-for-note on: learning a brand-new section note by note
  wait: boolean
  reason: "new" | "practice" | "graduate" | "review"
  accuracy: number

// Today's plan: the weakest unmastered sections first (error-driven), a
// brand-new section learns slow with wait-for-note, a section mid-streak
// pushes at its working rate, and one due mastered section reviews at full
// tempo. Interleaving comes from the cap: tomorrow's weakest three differ.
export lessonPlan := (
  sections: {name?: string}[]
  progress: PieceProgress
  now: number
  limit = 3
): PlanItem[] ->
  active: PlanItem[] := []
  reviews: PlanItem[] := []

  sections.forEach (s, i) ->
    name := s.name or `Section ${i + 1}`
    p := progress[i]
    if not p or p.passes is 0
      active.push {section: i, name, rate: RATE_STEPS[0], wait: true, reason: "new", accuracy: 0}
    else if p.mastered
      if now - p.lastAt > REVIEW_AFTER_MS
        reviews.push {section: i, name, rate: 1, wait: false, reason: "review", accuracy: p.accuracy}
    else
      reason := if p.cleanStreak > 0 then "graduate" else "practice"
      active.push {section: i, name, rate: targetRate(p), wait: false, reason, accuracy: p.accuracy}

  active.sort (a, b) -> a.accuracy - b.accuracy
  [...active.slice(0, limit), ...reviews.slice(0, 1)]
